import {
  TRIADE_PARTS, triadeReady, triadeSegments, withTriadeSynthesis,
  type Triade as TriadeContent, type TriadePart, type TriadeTip,
} from '@ads/shared';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { TextArea } from '../components/Field';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { useConsolidate, useToolReading } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';

/* ─────────────────────────────────────────────────────────────────────
   Tríade de Valor. O método monta a frase — "Acreditamos em …, por meio
   de …, resultando em …" —: os conectores são fixos, as três partes são
   da organização, e a frase-síntese acompanha as partes até alguém
   reescrevê-la. A Dica da IA só aparece quando há o que melhorar.
   ───────────────────────────────────────────────────────────────────── */

const ICON: Record<TriadePart, ReactNode> = { believe: <Ic.Heart />, through: <Ic.Gear />, result: <Ic.Bars /> };
const LABEL: Record<TriadePart | 'synthesis', string> = { believe: 'Acreditamos em', through: 'Por meio de', result: 'Resultando em', synthesis: 'Frase-síntese' };

export function Triade() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Tríade de Valor'], j: .33 });
  const nav = useNavigate();
  const { local, commit, flush, saveState, status } = useToolDraft('triade');
  const q = useToolReading('triade');
  const consolidate = useConsolidate();
  const [editing, setEditing] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const phrase = useRef<HTMLTextAreaElement>(null);

  const t = local;
  const segments = useMemo(() => (t ? triadeSegments(t) : []), [t]);
  const patch = (p: Partial<TriadeContent>) => { if (t) commit(withTriadeSynthesis({ ...t, ...p })); };
  useEffect(() => { if (editing) phrase.current?.focus(); }, [editing]);

  const r = q.data;
  const updating = !!r && (r.pending || r.stale);
  const tip = r?.reading ?? null;
  const tipKey = tip && !tip.ok ? `${tip.campo}:${tip.dica}` : null;
  const showTip = tip && !tip.ok && tipKey !== dismissed ? tip : null;
  const apply = (x: TriadeTip) => {
    if (!t || !x.campo || !x.sugestao) return;
    if (x.campo === 'synthesis') patch({ synthesis: x.sugestao, synthesisEdited: true });
    else patch({ [x.campo]: x.sugestao } as Partial<TriadeContent>);
    setDismissed(tipKey);
  };
  const handwritten = !!t?.synthesisEdited;

  return (
    <section className="view on" data-v="triade">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 03 / 10</b></div>
          <h1>Tríade de Valor</h1>
          <p className="lede">Refine o posicionamento da organização a partir da integração dos três gatilhos de valor.</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
        </div>
      </div>

      {!t ? <div className="loading-line" /> : (
        <div className="tri-grid">
          <div className="tri-main">
            <section className="card tri-card">
              <h3>Sua tríade de valor</h3>
              <p className="sub">Três partes, uma frase. O conector é fixo; o que vem depois dele é da organização.</p>
              {TRIADE_PARTS.map(p => (
                <div key={p.key} className={`tri-part p-${p.key}${showTip?.campo === p.key ? ' flag' : ''}`}>
                  <span className="tri-ic" aria-hidden="true">{ICON[p.key]}</span>
                  <TextArea id={`tri-${p.key}`} label={<><b>{p.lead}…</b><span className="tri-hint">{p.hint}</span></>}
                    value={t[p.key]} maxLength={240} placeholder={p.placeholder}
                    onChange={v => patch({ [p.key]: v } as Partial<TriadeContent>)} onBlur={flush} />
                </div>
              ))}
            </section>

            <section className={`card tri-syn${showTip?.campo === 'synthesis' ? ' flag' : ''}`}>
              <div className="gd-h">
                <h3>Frase-síntese</h3>
                <span className="tri-mode">{handwritten ? 'Reescrita por você' : 'Montada pela fórmula'}</span>
              </div>
              <p className="sub">A combinação dos três elementos em uma narrativa única e clara.</p>
              {editing || handwritten ? (
                <div className="ta tri-ta">
                  <textarea ref={phrase} value={t.synthesis} maxLength={500} aria-label="Frase-síntese" rows={4}
                    onChange={e => patch({ synthesis: e.target.value, synthesisEdited: true })}
                    onBlur={() => { flush(); setEditing(false); }} />
                  <div className="bar"><span className="hint" /><span className="cnt mono"><b>{t.synthesis.length}</b>/500</span></div>
                </div>
              ) : segments.length ? (
                <p className="tri-phrase" aria-live="polite">
                  {segments.map((s, i) => s.part ? <mark key={i} className={`p-${s.part}`}>{s.text}</mark> : <span key={i}>{s.text}</span>)}
                </p>
              ) : (
                <p className="gd-empty">Preencha as três partes: a frase se monta aqui, enquanto você escreve.</p>
              )}
              <div className="tri-foot">
                {handwritten ? (
                  <button type="button" className="d-link" onClick={() => { setEditing(false); patch({ synthesisEdited: false }); }}><Ic.Refresh />Remontar pela fórmula</button>
                ) : segments.length ? (
                  <button type="button" className="d-link" onClick={() => setEditing(true)}><Ic.Doc />Ajustar a frase</button>
                ) : <span />}
                {!(editing || handwritten) && segments.length > 0 && <span className="cnt mono"><b>{t.synthesis.length}</b>/500</span>}
              </div>
            </section>
          </div>

          <aside className="tri-side">
            <section className={`card tri-tip${showTip ? ' on' : ''}`} aria-live="polite">
              <div className="gd-h">
                <h3><Ic.Bulb />Dica da IA</h3>
                {updating && triadeReady(t) && <span className="obj-st busy"><span className="spin" />Lendo</span>}
              </div>
              {!triadeReady(t) ? (
                <p className="tri-tip-t">Seja objetivo e realista. Quanto mais concreta cada parte — um método, um público, um resultado que dá para ver —, mais forte fica a tríade. Com as três partes preenchidas, a IA lê a tríade e sugere um ajuste, se valer a pena.</p>
              ) : !tip ? (
                updating ? <div className="obj-skel"><i /><i /><i /></div>
                  : <p className="tri-tip-t">A IA lê a tríade alguns segundos depois de cada mudança.</p>
              ) : showTip ? (
                <div className={updating ? 'is-dim' : ''}>
                  {showTip.campo && <span className="tri-target">Em «{LABEL[showTip.campo]}»</span>}
                  <p className="tri-tip-t strong">{showTip.dica}</p>
                  {showTip.sugestao && (
                    <>
                      <blockquote className="tri-sug">{showTip.sugestao}</blockquote>
                      <div className="tri-actions">
                        <button type="button" className="btn-primary sm" onClick={() => apply(showTip)}>Aplicar sugestão<Ic.Check /></button>
                        <button type="button" className="btn-ghost sm" onClick={() => setDismissed(tipKey)}>Manter como está</button>
                      </div>
                    </>
                  )}
                  {!showTip.sugestao && <button type="button" className="d-link" onClick={() => setDismissed(tipKey)}>Entendi</button>}
                </div>
              ) : (
                <p className={`tri-ok${updating ? ' is-dim' : ''}`}><Ic.Check />{tip.ok ? 'A tríade está clara e coerente. Nada a sugerir por enquanto.' : 'Dica dispensada. A IA volta a ler quando a tríade mudar.'}</p>
              )}
              {r?.note && <p className="gd-note"><Ic.Warning />{r.note}</p>}
            </section>

            <section className="card tri-method">
              <h3>O método</h3>
              <ol className="tri-formula">
                <li className="p-believe"><span>Acreditamos em</span><i>no que nos move</i></li>
                <li className="p-through"><span>por meio de</span><i>como entregamos valor</i></li>
                <li className="p-result"><span>resultando em</span><i>o impacto que geramos</i></li>
              </ol>
              <p className="sub">A frase-síntese vai para o DNA e dá o tom das peças do Studio — manifesto, apresentação, site.</p>
            </section>
          </aside>
        </div>
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy/diferenciais'); }}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending}
          onClick={() => { flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/visao') }); }}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}
